/**
 * Human Resources · my check-in (for everybody, on their phone).
 *
 * One big button: the phone finds where it is, the platform decides whether
 * that is inside a location this person may check in at, and today's
 * attendance follows. Below it, the person's own requests to HR with their
 * answers, and a form to ask for leave, a letter or a change — each answer
 * also comes by SMS and email.
 *
 * Whether the phone may be used at all is HR's decision on the person's
 * file; somebody set to a face or fingerprint reader is told so here.
 */
import { useMemo, useState } from "react";
import { useHrClockMe, useHrMe, useHrSelfRequest } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, EmptyState, Field, Input, Select, Skeleton } from "../../design/components";
import { locate, METHOD, POLICY_METHODS, REQUEST_TYPES, type Located } from "../../domain/hrClock";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { addDays, Chip, h, niceDate, StatusChip, todayIso } from "./kit";
import { HrError, s, SampleBanner } from "./shared";

interface Fix {
  lat: number;
  lon: number;
  accuracy: number | null;
  demo?: boolean;
}

export function HrMePage() {
  const session = useSession();
  const q = useHrMe();
  const clock = useHrClockMe();
  const [fix, setFix] = useState<Fix | null>(null);
  const [finding, setFinding] = useState(false);
  const [geoErr, setGeoErr] = useState<string | null>(null);
  const me = q.data;
  const where: Located | null = useMemo(
    () => (me && fix ? locate(me.locations, fix.lat, fix.lon, fix.accuracy) : null),
    [me, fix],
  );
  const lastIn = me?.events.filter((e) => e.direction === "in").at(-1);
  const lastOut = me?.events.filter((e) => e.direction === "out").at(-1);
  const inNow = !!lastIn && (!lastOut || lastOut.at < lastIn.at);

  useScreenContext(
    me
      ? {
          route: "/hr-me",
          title: "My check-in",
          summary: [
            me.today?.checkIn
              ? t("You checked in at {t} today.", { t: me.today.checkIn.slice(0, 5) })
              : t("You have not checked in today."),
          ],
          offers: [{ label: t("How much leave do I have?"), ask: "how much annual leave do I have left?" }],
          worksInFull: true,
        }
      : null,
  );

  const find = () => {
    setGeoErr(null);
    setFinding(true);
    if (!("geolocation" in navigator)) {
      setFinding(false);
      setGeoErr(t("This browser cannot tell where the phone is."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setFinding(false);
        setFix({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy });
      },
      (e) => {
        setFinding(false);
        setGeoErr(
          e.code === 1
            ? t("Location is off for this app. Turn it on in phone settings and try again.")
            : t("Couldn't find your location. Move near a window or outside and try again."),
        );
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const go = (direction: "in" | "out") => {
    if (!fix) return;
    clock.mutate({
      direction,
      lat: fix.lat,
      lon: fix.lon,
      accuracyM: fix.accuracy,
      clientId: `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      device: navigator.userAgent.slice(0, 120),
    });
  };

  return (
    <Page eyebrow={t("Human Resources")} title="My check-in">
      <SampleBanner />
      {q.isLoading && <Skeleton rows={5} />}
      {q.error && <HrError error={q.error} retry={() => void q.refetch()} />}
      {me && (
        <>
          <section className={h.hero} data-testid="hr-me-hero">
            <h2>{t("Hello, {name}", { name: me.employee.name.split(" ")[0] })} 👋</h2>
            <p>
              {me.today?.checkIn
                ? t("Checked in today at {t}{late}.", {
                    t: me.today.checkIn.slice(0, 5),
                    late: me.today.minutesLate ? ` (${t("{n} min late", { n: me.today.minutesLate })})` : "",
                  })
                : t("You have not checked in today.")}
              {me.today?.checkOut ? ` ${t("Checked out at {t}.", { t: me.today.checkOut.slice(0, 5) })}` : ""}
            </p>
            {!me.phoneAllowed ? (
              <div className={s.coverage} data-testid="hr-me-reader">
                <div>
                  <b>
                    {me.clockMethods?.length
                      ? t("Check in at the {what}.", {
                          what: me.clockMethods
                            .map((m) => t(POLICY_METHODS.find((x) => x.key === m)?.label ?? m).toLowerCase())
                            .join(t(" or ")),
                        })
                      : t("HR hasn't set your check-in method. Use a face or fingerprint reader for now.")}
                  </b>
                  {me.clockBasis ? ` ${t(me.clockBasis)}` : ""}
                </div>
              </div>
            ) : (
              <>
                <Button onClick={find} disabled={finding} data-testid="hr-me-find">
                  📍 {finding ? t("Finding where you are…") : fix ? t("Find me again") : t("Find where I am")}
                </Button>
                {session.source.kind === "mock" && (
                  <Button
                    onClick={() => {
                      const f = me.locations[0];
                      if (f) setFix({ lat: f.lat + 0.0003, lon: f.lon, accuracy: 12, demo: true });
                    }}
                    data-testid="hr-me-demo-fix"
                  >
                    {t("Pretend I am at work (demonstration)")}
                  </Button>
                )}
                {geoErr && <p className={s.err}>{geoErr}</p>}
                {where && (
                  <div className={s.coverage} data-testid="hr-me-where">
                    <div>
                      {where.inside ? (
                        <b style={{ color: "var(--green)" }}>
                          ✓{" "}
                          {t("You are at {place} ({d} m from its center).", {
                            place: where.fence?.name ?? "",
                            d: Math.round(where.distanceM ?? 0),
                          })}
                        </b>
                      ) : (
                        <b style={{ color: "var(--red)" }}>✗ {t(where.reason ?? "Outside every check-in location")}</b>
                      )}{" "}
                      {fix?.accuracy != null && (
                        <span className={s.dim}>({t("accurate to {m} m", { m: Math.round(fix.accuracy) })})</span>
                      )}
                    </div>
                  </div>
                )}
                <div className={h.actions}>
                  <button
                    type="button"
                    className={h.action}
                    disabled={!where?.inside || clock.isPending}
                    onClick={() => go("in")}
                    style={{ opacity: where?.inside ? 1 : 0.5 }}
                    data-testid="hr-me-in"
                  >
                    <span className={h.actionIcon} style={{ ["--tint" as string]: "var(--green)" }}>
                      ↘
                    </span>
                    <span>
                      {t("Check in")}
                      <small>{inNow ? t("You are already in") : t("Start of the day")}</small>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={h.action}
                    disabled={!where?.inside || clock.isPending}
                    onClick={() => go("out")}
                    style={{ opacity: where?.inside ? 1 : 0.5 }}
                    data-testid="hr-me-out"
                  >
                    <span className={h.actionIcon} style={{ ["--tint" as string]: "var(--amber)" }}>
                      ↗
                    </span>
                    <span>
                      {t("Check out")}
                      <small>{t("End of the day")}</small>
                    </span>
                  </button>
                </div>
                {clock.error && (
                  <p className={s.err}>{clock.error instanceof Error ? clock.error.message : String(clock.error)}</p>
                )}
                {clock.data?.place && (
                  <p className={s.ok} data-testid="hr-me-done">
                    ✓{" "}
                    {t("Recorded at {place}, {t}.", {
                      place: clock.data.place,
                      t: new Date(clock.data.at ?? "").toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                    })}
                  </p>
                )}
              </>
            )}
          </section>
          <div className={h.grid2}>
            <Card title={t("Today")}>
              {me.events.length === 0 ? (
                <EmptyState>{t("Nothing yet today.")}</EmptyState>
              ) : (
                <ul className={h.list}>
                  {me.events.map((e, i) => (
                    <li key={i} className={h.item}>
                      <span style={{ fontSize: 18 }}>{METHOD[e.method]?.icon}</span>
                      <span className={h.itemMain}>
                        <b>
                          {e.direction === "in" ? t("Checked in") : t("Checked out")} ·{" "}
                          {new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </b>
                        <span>
                          {t(METHOD[e.method]?.label ?? e.method)}
                          {e.place ? ` · ${e.place}` : ""}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <p className={s.dim}>
                {t("Where you may check in")}: {me.locations.map((l) => l.name).join(", ") || "—"}
              </p>
            </Card>
            <AskHr hasPhone={me.hasPhone} hasEmail={me.hasEmail} />
          </div>
          <Card title={t("My requests")} sub={t("Every answer is also sent to you by SMS and email.")}>
            {me.requests.length === 0 ? (
              <EmptyState>{t("You have not asked HR for anything yet.")}</EmptyState>
            ) : (
              <ul className={h.list}>
                {me.requests.map((r) => (
                  <li key={r.id} className={h.item} data-testid="hr-me-request">
                    <span className={h.itemMain}>
                      <b>{t(r.title)}</b>
                      <span>
                        {r.startsOn
                          ? `${niceDate(r.startsOn)}${r.endsOn && r.endsOn !== r.startsOn ? ` – ${niceDate(r.endsOn)}` : ""} · `
                          : ""}
                        {t("asked {d}", { d: niceDate(r.occurredOn) })}
                        {r.reference ? ` · ${r.reference}` : ""}
                        {r.decisionNote ? ` · ${t("HR")}: ${r.decisionNote}` : ""}
                      </span>
                    </span>
                    <StatusChip status={r.status as "requested"} />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </Page>
  );
}

function AskHr({ hasPhone, hasEmail }: { hasPhone: boolean; hasEmail: boolean }) {
  const ask = useHrSelfRequest();
  const [type, setType] = useState("annual");
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [from, setFrom] = useState(addDays(todayIso(), 7));
  const [to, setTo] = useState(addDays(todayIso(), 11));
  const def = REQUEST_TYPES.find((r) => r.key === type);
  const leave = def?.kind === "leave";
  return (
    <Card title={t("Ask HR")} sub={t("Leave, a letter, or a change. HR decides.")}>
      <form
        className={s.dialogForm}
        onSubmit={(e) => {
          e.preventDefault();
          ask.mutate(
            {
              type,
              title: title.trim() || (def?.label ?? "Request"),
              details: details.trim() || null,
              startsOn: leave ? from : null,
              endsOn: leave ? to : null,
            },
            {
              onSuccess: () => {
                setTitle("");
                setDetails("");
              },
            },
          );
        }}
      >
        <div className={s.form}>
          <Field label="What do you need?">
            <Select value={type} onChange={(e) => setType(e.target.value)} data-testid="hr-ask-type">
              {REQUEST_TYPES.map((r) => (
                <option key={r.key} value={r.key}>
                  {t(r.label)}
                </option>
              ))}
            </Select>
          </Field>
          {leave && (
            <>
              <Field label="First day">
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </Field>
              <Field label="Last day">
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </Field>
            </>
          )}
          {!leave && (
            <Field label="In a few words">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t(def?.label ?? "")} />
            </Field>
          )}
          <Field label="Details (optional)">
            <Input value={details} onChange={(e) => setDetails(e.target.value)} data-testid="hr-ask-details" />
          </Field>
        </div>
        {(!hasPhone || !hasEmail) && (
          <p className={s.note}>
            {!hasPhone && !hasEmail
              ? t(
                  "HR has no mobile number or email for you. Ask HR to add them so answers reach you.",
                )
              : !hasPhone
                ? t("HR has no mobile number for you; answers will come by email only.")
                : t("HR has no email for you; answers will come by SMS only.")}
          </p>
        )}
        {ask.error && <p className={s.err}>{ask.error instanceof Error ? ask.error.message : String(ask.error)}</p>}
        {ask.isSuccess && (
          <p className={s.ok}>{t("Sent to HR. You'll get an SMS and email when it's decided.")}</p>
        )}
        <div className={s.formFoot}>
          <Button type="submit" variant="primary" disabled={ask.isPending} data-testid="hr-ask-send">
            {t("Send to HR")}
          </Button>
        </div>
      </form>
      <Chip>{t("Requests go to HR for a decision.")}</Chip>
    </Card>
  );
}
